import { debug } from 'lib/logging'

/**
 * Custom error class for API response errors
 * @extends Error
 */
export class ResponseError extends Error {
  /** HTTP status code of the response */
  status: number

  /** Response body content */
  body: string

  /**
   * Creates a new ResponseError instance
   * @param {Object} params - Error parameters
   * @param {number} params.status - HTTP status code
   * @param {string} params.body - Response body content
   */
  constructor({ status, body }) {
    super(body)
    this.status = status
    this.body = body
  }
}

/**
 * Makes an HTTP request to the API
 * @param {string} method - HTTP method (get, post, delete, put, patch)
 * @param {string} path - API endpoint path
 * @param {any} [body] - Request body data
 * @returns {Promise<any>} Parsed JSON response
 * @throws {ResponseError} When response status is >= 400
 */
async function request(method: `get` | `post` | `delete` | `put` | `patch`, path: string, body?: any) {
  let resp = await window.fetch(path, {
    method,
    headers: { 'content-type': `application/json` },
    body: body && JSON.stringify(body),
  })
  if (resp.status >= 400) {
    throw new ResponseError({ status: resp.status, body: await resp.text() })
  }
  return resp.json()
}

/**
 * API client for making HTTP requests
 */
export const api = {
  /**
   * Makes a POST request to the API
   * @param {string} path - API endpoint path
   * @param {any} body - Request body data
   * @returns {Promise<any>} Parsed JSON response
   */
  post: async (path: string, body: any) => {
    debug(`api`, `api host`, process.env.HOST)
    return request(`post`, path, body)
  },

  /**
   * Makes a GET request to the API
   * @param {string} path - API endpoint path
   * @returns {Promise<any>} Parsed JSON response
   */
  get: async (path: string) => {
    return request(`get`, path)
  },

  /**
   * Makes a DELETE request to the API
   * @param {string} path - API endpoint path
   * @returns {Promise<any>} Parsed JSON response
   */
  delete: async (path: string) => {
    return request(`delete`, path)
  },
}
